<template>
  <div
    v-show="(!usingNewLayout || isLeftDrawerOpen) && !showRecentTemplates"
    ref="outerContainer"
    class="tw-relative"
    :class="{
      'tw-px-4': !usingNewLayout,
      'tw-flex tw-flex-col tw-flex-1 tw-min-h-0': usingNewLayout,
    }"
  >
    <div
      v-if="showOverviewTree && showBuiltInSearch"
      ref="navigation"
      class="tw-shrink-0 tw-bg-white tw-pb-3 tw-py-3 tw-z-10"
      :class="{
        'tw-sticky tw-top-0 tw-flex tw-justify-between tw-gap-4':
          !usingNewLayout,
        'tw-pr-2': usingNewLayout,
      }"
    >
      <SfButtonIcon
        v-if="!usingNewLayout"
        icon="arrow-left"
        @click="drawerStore.closeLeftDrawer"
      />
      <div :class="{ 'tw-flex-1': !usingNewLayout }">
        <OverviewNavigationSearch
          :can-be-focused="true"
          :compact="usingNewLayout"
          data-testid="overview-search"
        />
      </div>
    </div>
    <div
      ref="innerScrollable"
      class="tw-relative tw-z-0"
      :class="{
        'tw-flex-1 tw-min-h-0 tw-overflow-y-auto': usingNewLayout,
        'tw-pr-1': usingNewLayout && hasScrollbar,
        'tw-pr-2': usingNewLayout && !hasScrollbar,
      }"
    >
      <template v-if="showOverviewTree">
        <hr
          v-if="usingNewLayout"
          class="
            tw-mt-0
            tw-mb-2
            tw-border-solid
            tw-border-x-0
            tw-border-t-0
            tw-border-b
            tw-border-sf-slate-lighter-1
          "
          data-testid="overview-checklists-divider-top"
        />
        <OverviewNavigationChecklists
          v-if="usingNewLayout && !hasAnyFilterApplied"
          :checklists="checklists"
          :current-path="currentPath"
        />
        <OverviewNavigation
          :variant="usingNewLayout ? 'slim' : 'default'"
          :expanded-overview-groups="expandedOverviewGroups"
          :overview-tree="overviewTreeWithSelected"
          :is-loading="loading"
          :ctrl-click="ctrlClick"
          :is-dimmed="isDimmed"
          @item-clicked="treeMenuItemClicked"
          @item-expanded="handleItemExpansion"
          @item-toggled="handleItemToggled"
        >
          <template #accent="{ itemForSlot }">
            <OverviewNavigationActiveHighlight
              :highlighted-id="highlightedId"
              :highlighted-type="highlightedType"
              :item-id="
                itemForSlot.type === 'account_entry'
                  ? itemForSlot.accountEntryId
                  : itemForSlot.id
              "
              :item-type="itemForSlot.type"
              :scroll-into-view-timestamp="scrollIntoViewTimestamp"
              @scroll-offset-element="handleScrollOffsetElement"
            />
          </template>
          <template #itemSlot="{ itemForSlot }">
            <div class="sf-tree-menu__item-icon tw-contents">
              <OverviewNavigationCheckmark
                v-if="isInternalUser"
                :item-id="
                  itemForSlot.type === 'account_entry'
                    ? itemForSlot.accountEntryId
                    : itemForSlot.id
                "
                @review-status-updated="debouncedResetOverviewDataCache"
              />
              <OverviewNavigationReconciliationStatus
                v-if="isWorkflowOverviewItem(itemForSlot)"
                :item="itemForSlot"
              />
            </div>
          </template>
        </OverviewNavigation>
      </template>
    </div>
  </div>
  <div
    v-if="
      usingNewLayout &&
      showRecentTemplates &&
      isLeftDrawerOpen &&
      !!ledgerId &&
      !highlightedId &&
      !dependsOnReviewStore
    "
    class="tw--mx-2 tw-pr-2"
  >
    <hr
      class="
        tw-mt-0
        tw-mb-2
        tw-border-solid
        tw-border-x-0
        tw-border-t-0
        tw-border-b
        tw-border-sf-slate-lighter-1
      "
      data-testid="overview-checklists-divider-top"
    />
    <OverviewNavigationRecentTemplates
      :ledger-id="ledgerId"
      :workflow-id="workflowId"
    />
  </div>
</template>
<script lang="ts">
function markSelected(
  items: Array<WorkflowOverviewItem | WorkflowOverviewGroup>,
  id: number,
  itemClass: string
): Array<WorkflowOverviewItem | WorkflowOverviewGroup> {
  return items.map((item) => {
    if (item.type === 'group') {
      return { ...item, children: markSelected(item.children, id, itemClass) }
    }
    const effectiveId =
      item.type === 'account_entry' ? item.accountEntryId : item.id
    return {
      ...item,
      selected: effectiveId === id && item.type === itemClass,
    }
  })
}
</script>

<script setup lang="ts">
import type {
  WorkflowOverviewItem,
  WorkflowOverviewItemType,
  WorkflowOverviewGroup,
  UserType,
} from '@app/application/workflow-overview-navigation/types'
import type { WorkflowFilterStatus } from '@app/shared/types/sf-data'

import isEqual from 'lodash/isEqual'
import debounce from 'lodash/debounce'
import { storeToRefs } from 'pinia'
import {
  computed,
  onBeforeUnmount,
  nextTick,
  ref,
  watch,
  watchEffect,
} from 'vue'
import { useMediaQuery } from '@vueuse/core'

import OverviewNavigation from './overview-navigation.vue'
import OverviewNavigationRecentTemplates from './overview-navigation-recent-templates.vue'
import useOverviewMenu from '../composables/use-overview-menu-click'
import { useCurrentHighlightedItem } from '../composables/use-current-highlighted-item'
import { determineFilterValue, findAccountId } from '../utils'
import OverviewNavigationActiveHighlight from './overview-navigation-active-highlight.vue'
import OverviewNavigationChecklists from './overview-navigation-checklists.vue'
import OverviewNavigationCheckmark from './overview-navigation-checkmark.vue'
import OverviewNavigationReconciliationStatus from './overview-navigation-reconciliation-status.vue'
import OverviewNavigationSearch from './overview-navigation-search.vue'
import { SfLog } from '@app/adapters/logger'
import { SfButtonIcon } from '@app/shared/vue/components/sf-button/index'

import useSfPolling from '@app/shared/vue/composables/use-sf-polling'
import { isUsingNewLayout } from '@app/adapters/silverfin'
import { useWorkflowOverview } from '@app/application/workflow-overview-navigation/store/overview'
import { useWorkflowOverviewSearch } from '@app/application/workflow-overview-navigation/store/search'
import { useWorkflowReviewFilter } from '@app/application/workflows/screens/workflow-review/store/pinia/workflow-review-filter'
import { useWorfklowReviewItems } from '@app/application/workflows/screens/workflow-review/store/pinia/workflow-review-items'
import { useDrawer } from '@app/shared/pinia/drawer'
import { useDrawerScrollPosition } from '../composables/use-drawer-scroll-position'
import { UserTypes } from '../types'
import { useWorkflowProgress } from '../store/workflow-progress'
import { isWorkflowOverviewItem } from '../predicates'
import { LeftDrawerCacheService } from '@app/shared/services/left-drawer-cache'

defineOptions({
  name: 'OverviewNavigationDrawer',
})

interface Props {
  allowedFilterValues?: WorkflowFilterStatus[]
  // In WF Review show-all means starred or remarked on
  defaultFilterParamValue?: WorkflowFilterStatus
  dependsOnReviewStore: boolean
  highlightedId?: number
  highlightedType?: WorkflowOverviewItemType
  ledgerId: number
  messageBusChannel?: string | null
  messageBusLastId?: number
  userType?: UserType
  workflowId: number
  showWorkflowTree?: boolean
  ctrlClick?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  allowedFilterValues: () => [
    'show-all',
    'todos',
    'checks',
    'unreconciled',
    'not_prepared',
    'unreviewed_by_me',
    'prepared_unreviewed_by_me',
    'starred',
  ],
  defaultFilterParamValue: 'show-all',
  dependsOnReviewStore: false,
  highlightedId: undefined,
  highlightedType: undefined,
  messageBusChannel: null,
  messageBusLastId: -1,
  showWorkflowTree: true,
  userType: UserTypes.INTERNAL_USER,
})

const lastFetchedWorkflowId = ref(0)
const loading = ref(true)
const navigation = ref<HTMLElement | null>(null)
const outerContainer = ref<HTMLElement | null>(null)
const innerScrollable = ref<HTMLElement | null>(null)
const scrollableContainer = ref<HTMLElement | null>(null)
const hasScrollbar = ref(false)

const scrollbarObserver = new ResizeObserver(() => {
  requestAnimationFrame(() => {
    if (innerScrollable.value) {
      hasScrollbar.value =
        innerScrollable.value.scrollHeight > innerScrollable.value.clientHeight
    }
  })
})
const requestedData = ref(false)
const { startPolling } = useSfPolling()
const drawerStore = useDrawer()

const filterStore = useWorkflowReviewFilter()
const overviewStore = useWorkflowOverview()
const searchStore = useWorkflowOverviewSearch()
const workflowProgressStore = useWorkflowProgress()
const reviewItemsStore = useWorfklowReviewItems()

const { treeMenuItemClicked: scrollToTreeMenuItem } = useOverviewMenu()

const { isLeftDrawerOpen } = storeToRefs(drawerStore)
const { filter } = storeToRefs(filterStore)
const { hasHeaderSearch } = storeToRefs(searchStore)
const {
  expandedOverviewGroups,
  checklists,
  filteredGroupsById,
  overviewTreeData,
  starredItemStatus,
  hasAnyFilterApplied,
} = storeToRefs(overviewStore)
const currentPath = ref(window.location.pathname)
const { getStarredItemKey } = overviewStore
const { currentReviewItem } = storeToRefs(reviewItemsStore)

let messageBusUnsubscribeHandler: null | (() => void)

const isPulseUser = computed<boolean>(
  () => props.userType === UserTypes.PULSE_USER
)
const isInternalUser = computed<boolean>(
  () => props.userType === UserTypes.INTERNAL_USER
)

const usingNewLayout = isUsingNewLayout()

// Making sure we do not call the API too many times when multiple events are emitted in a short time span
// (e.g. when recon status and review status are updated at the same time)
const debouncedResetOverviewDataCache = debounce(resetOverviewDataCache, 800)

const {
  scrollIntoViewTimestamp,
  hasSetActiveItemOnce,
  handleScrollOffsetElement,
} = useDrawerScrollPosition(scrollableContainer, loading, props.workflowId)

const showRecentTemplates = computed(() => !props.showWorkflowTree)

const showBuiltInSearch = computed(
  () => !(usingNewLayout && hasHeaderSearch.value)
)

const showOverviewTree = computed(
  () =>
    !usingNewLayout ||
    !showRecentTemplates.value ||
    !!props.highlightedId ||
    props.dependsOnReviewStore
)

const { currentId, currentItemClass } = useCurrentHighlightedItem(
  () => props.highlightedId,
  () => props.highlightedType
)

const overviewTreeWithSelected = computed<
  Array<WorkflowOverviewItem | WorkflowOverviewGroup>
>(() => {
  if (!currentId.value || !currentItemClass.value) return overviewTreeData.value
  return markSelected(
    overviewTreeData.value,
    currentId.value,
    currentItemClass.value
  )
})

function isDimmed(item: WorkflowOverviewItem | WorkflowOverviewGroup): boolean {
  if (item.type === 'group') {
    return false
  }

  const key = getStarredItemKey(item)

  if (key && starredItemStatus.value[key] !== undefined) {
    return starredItemStatus.value[key] === false
  }

  return false
}

setupSubscription()

if (usingNewLayout && !isPulseUser.value) {
  overviewStore.fetchChecklists({
    ledgerId: props.ledgerId,
    workflowId: props.workflowId,
  })
}

if (isPulseUser.value) {
  const isLargeScreen = useMediaQuery('(min-width: 1280px)')
  // For Pulse users:
  //   - On Desktop: open left sidepane IF the user doesn't have any preference set
  //   - On Mobile: force-close BOTH drawers regardless of previous preference.
  //     Use the `WithoutPersisting` variant for the right drawer so the
  //     user's stored preference is preserved for when they return on a
  //     wider screen. In the legacy layout the global drawer:close handler
  //     in `application/behaviors/drawers/layout.ts` cascaded a single
  //     close into both drawers; the new-layout handler is direction-scoped
  //     so the right drawer has to be closed explicitly.
  window.requestAnimationFrame(() => {
    if (isLargeScreen.value) {
      drawerStore.openLeftDrawerDefault()
    } else {
      drawerStore.closeLeftDrawer()
      drawerStore.closeRightDrawerWithoutPersisting(false)
    }
  })
}

window.eventHub.$on('sf-left-overview-navigation:reload', fetchInitialData)
window.eventHub.$on(
  'sf-left-overview-navigation:starred-changed',
  handleStarredChanged
)
window.eventHub.$on('sf-recon-status-updated', handleReconStatusUpdated)

onBeforeUnmount(() => {
  window.eventHub.$off('sf-left-overview-navigation:reload', fetchInitialData)
  window.eventHub.$off(
    'sf-left-overview-navigation:starred-changed',
    handleStarredChanged
  )
  window.eventHub.$off('sf-recon-status-updated', handleReconStatusUpdated)

  removeMessageBusSubscription()
  debouncedResetOverviewDataCache.cancel()
  scrollbarObserver.disconnect()
})

function handleReconStatusUpdated(payload: {
  itemClass: WorkflowOverviewItemType
  itemId: number
  reconciliationSummaryHtml?: string
}): void {
  if (!payload.reconciliationSummaryHtml) {
    return
  }

  if (
    props.highlightedType === payload.itemClass &&
    props.highlightedId === payload.itemId
  ) {
    const objectType =
      payload.itemClass === 'account_entry' ? 'account' : payload.itemClass
    let objectId = payload.itemId

    if (payload.itemClass === 'account_entry') {
      const accountId = findAccountId(overviewTreeData.value, payload.itemId)
      if (accountId) {
        objectId = accountId
      } else {
        return
      }
    }

    workflowProgressStore.updateItemFromHtml(
      objectType,
      objectId,
      payload.reconciliationSummaryHtml
    )
  }

  if (usingNewLayout) {
    debouncedResetOverviewDataCache()
  }
}

async function resetOverviewDataCache() {
  const cacheKeyPrefix = LeftDrawerCacheService.buildCacheKey({
    currentFirmId: window.Silverfin.currentFirmId,
    currentCompanyId: window.Silverfin.currentCompanyId,
    ledgerId: window.Silverfin.currentLedgerId(),
    workflowId: props.workflowId,
  })

  const prefix = LeftDrawerCacheService.createPrefix('overview', cacheKeyPrefix)
  LeftDrawerCacheService.deleteCacheByPredicate((k) => k.startsWith(prefix))

  // calling to warm cache
  await fetchData()
}

async function fetchData() {
  const filterParam = determineFilterValue({
    incomingFilter: filter.value,
    allowedFilterValues: props.allowedFilterValues,
    defaultFilterValue: props.defaultFilterParamValue,
  })

  try {
    await overviewStore.fetchOverview({
      filter: filterParam,
      isPulseUser: isPulseUser.value,
      workflowId: props.workflowId,
      ledgerId: props.ledgerId,
      includeReviewStatus: true,
      highlightedItemId: props.highlightedId ?? undefined,
      highlightedItemType: props.highlightedType ?? undefined,
    })
    loadExpandedState()
  } catch {
    SfLog.error(
      `Failed to fetch overview tree for workflow ${props.workflowId} and ledger ${props.ledgerId}`
    )
  }
}

async function fetchInitialData() {
  loading.value = true
  requestedData.value = true
  lastFetchedWorkflowId.value = props.workflowId

  await fetchData()

  loading.value = false
}

function handleStarredChanged(eventData: {
  type: string
  id: number
  starred: boolean
}) {
  overviewStore.updateItemStarredStatus(
    eventData.type,
    eventData.id,
    eventData.starred
  )

  if (!isPulseUser.value) {
    workflowProgressStore.toggleStarredStatus(
      eventData.type,
      eventData.id,
      eventData.starred
    )
  }

  if (usingNewLayout) {
    debouncedResetOverviewDataCache()
  }
}

/**
 * After the parents expand we send a signal to the highlight component so that it sends back
 * its top offset. This way we know how much we need to scroll to make the highlighted item visible.
 */
function forceHighlightedItemIntoView() {
  nextTick(() => {
    scrollIntoViewTimestamp.value = Date.now()
  })
}

function handleItemToggled(groupId: string): void {
  overviewStore.toggleOverviewGroup(groupId.toString())
}

/**
 * The tree menu itself notifies that it wants some parents expanded.
 * This is happening because it contains an item that is highlighted and we need to expand the hierarchy until that item.
 * @param itemId - The group id of one of the highlighted item's ancestors.
 */
function handleItemExpansion(itemId: number): void {
  overviewStore.expandOverviewGroup(itemId.toString())

  forceHighlightedItemIntoView()
}

function treeMenuItemClicked(
  item: WorkflowOverviewItem | WorkflowOverviewGroup
) {
  if (!props.dependsOnReviewStore) {
    const workflowItem = item as WorkflowOverviewItem
    const path = workflowItem.path
    if (path) {
      window.Turbolinks.visit(path)
    }
  } else {
    scrollToTreeMenuItem(item)
  }
}

function loadExpandedState() {
  overviewStore.applyInitialExpandState()
}

function setupSubscription(): void {
  removeMessageBusSubscription()

  if (!props.messageBusChannel) {
    return
  }

  const receiveHandler = workflowProgressStore.receiveWorkflowProgress

  messageBusUnsubscribeHandler = startPolling(
    props.messageBusChannel,
    // In the new layout we subtract 1 so the last message is re-delivered,
    // allowing its payload to rebuild the reconciliation icons on initial load.
    usingNewLayout ? props.messageBusLastId - 1 : props.messageBusLastId,
    () => true,
    receiveHandler
  )
}

function removeMessageBusSubscription(): void {
  if (messageBusUnsubscribeHandler) {
    messageBusUnsubscribeHandler()
    messageBusUnsubscribeHandler = null
  }
}

watch(
  usingNewLayout ? innerScrollable : outerContainer,
  (newValue) => {
    if (newValue) {
      scrollableContainer.value = usingNewLayout
        ? newValue
        : newValue.closest('aside')
    }
  },
  { immediate: true }
)

if (usingNewLayout) {
  watch(
    innerScrollable,
    (el, prevEl) => {
      if (prevEl) scrollbarObserver.unobserve(prevEl)
      if (el) scrollbarObserver.observe(el)
    },
    { immediate: true }
  )
}

watch(isLeftDrawerOpen, (newValue, oldValue) => {
  // Each time the drawer closes, reset the flag
  if (!newValue && newValue !== oldValue) {
    hasSetActiveItemOnce.value = false
  }
})

watch(filteredGroupsById, (newValue, oldValue) => {
  if (
    Object.keys(newValue).length > 0 &&
    newValue !== oldValue &&
    !isEqual(newValue, oldValue)
  ) {
    Object.keys(filteredGroupsById.value).forEach((group) => {
      overviewStore.expandOverviewGroup(group)
    })
  }
})

watchEffect(() => {
  // In WF Review, the first time the drawer opens, expand and focus the current review item
  if (
    props.dependsOnReviewStore &&
    !hasSetActiveItemOnce.value &&
    currentReviewItem.value &&
    isLeftDrawerOpen.value &&
    overviewTreeData.value.length > 0
  ) {
    overviewStore.expandItemParents(
      currentReviewItem.value.id,
      currentReviewItem.value.itemClass
    )

    forceHighlightedItemIntoView()
  }
})

watchEffect(() => {
  // Reloaded in dev HMR mode / browser back button, no need to request data again
  if (overviewTreeData.value.length > 0) {
    requestedData.value = true
    loading.value = false
  }

  // Force data fetch when the browser back button is used to navigate to a different workflow
  if (
    overviewTreeData.value.length > 0 &&
    lastFetchedWorkflowId.value !== props.workflowId &&
    isLeftDrawerOpen.value
  ) {
    fetchInitialData()
  }

  // Opened for the first time while in current page
  if (
    overviewTreeData.value.length === 0 &&
    isLeftDrawerOpen.value &&
    !requestedData.value
  ) {
    fetchInitialData()
  }
})
</script>
